import React, { useEffect, useState } from "react";
import {
  requestGetRewardsRedemption,
  usePointDispatch,
  usePointState,
} from "../../Context";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DetailSkeleton from "../../Components/DetailSkeleton";
import dayjs from "dayjs";
import {
  CheckIcon,
  DuplicateIcon,
  LocationMarkerIcon,
  MailIcon,
  PhoneIcon,
  XIcon,
} from "@heroicons/react/outline";
import { NumericFormat } from "react-number-format";
import { classNames, formatIndonesianPhone } from "../../Utils";
import Badge from "../../Components/Badge";
import { Alert } from "../../Components/Alert";

const initialSteps = [
  {
    name: "orderCreated",
    description: "rewardRedemptionRequested",
    status: "current",
  },
  {
    name: "processing",
    description: "orderBeingPrepared",
    status: "upcoming",
  },
  {
    name: "shipping",
    description: "itemShippingToDestination",
    status: "upcoming",
  },
  {
    name: "in_transit",
    description: "itemBeingDelivered",
    status: "upcoming",
  },
  {
    name: "delivery",
    description: "itemDeliveredToRecipient",
    status: "upcoming",
  },
  {
    name: "voucherCode",
    description: "voucherCodeIssued",
    status: "upcoming",
  },
  {
    name: "completed",
    description: "topUpCompleted",
    status: "upcoming",
  },
];

const RewardRedemptionDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const pointDispatch = usePointDispatch();

  const [steps, setsteps] = useState([]);
  const [isCopied, setIsCopied] = useState(false);

  const { redemptionDetail, redemptionDetailLoading, redemptionDetailError } =
    usePointState();

  const isAmount = redemptionDetail?.voucher?.voucher?.type === "AMOUNT";

  useEffect(() => {
    if (id) {
      requestGetRewardsRedemption(pointDispatch, { id });
    }
  }, [pointDispatch, id]);

  useEffect(() => {
    if (redemptionDetail?.status && redemptionDetail?.rewardProduct?.type) {
      let updatedSteps = [...initialSteps];

      updatedSteps =
        redemptionDetail.rewardProduct.type === "VOUCHER"
          ? [initialSteps[0], initialSteps[5]]
          : redemptionDetail.rewardProduct.type === "MOBILE_RELOAD"
          ? [initialSteps[0], initialSteps[6]]
          : initialSteps.slice(0, 5);

      if (redemptionDetail.rewardProduct.type === "VOUCHER") {
        if (redemptionDetail.status === "PENDING") {
          updatedSteps[0].status = "current";
        } else if (redemptionDetail.status === "ISSUED") {
          updatedSteps[0].status = "complete";
          updatedSteps[1].status = "complete";
        }
      }
      if (redemptionDetail.rewardProduct.type === "MOBILE_RELOAD") {
        if (redemptionDetail.status === "PENDING") {
          updatedSteps[0].status = "current";
        } else if (redemptionDetail.status === "COMPLETED") {
          updatedSteps[0].status = "complete";
          updatedSteps[1].status = "complete";
        }
      } else {
        if (redemptionDetail.status === "PENDING") {
          updatedSteps[0]["status"] = "current";
        } else if (redemptionDetail.status === "PACKED") {
          updatedSteps[0]["status"] = "complete";
          updatedSteps[1]["status"] = "current";
        } else if (redemptionDetail.status === "SHIPPED") {
          updatedSteps[0]["status"] = "complete";
          updatedSteps[1]["status"] = "complete";
          updatedSteps[2]["status"] = "current";
        } else if (redemptionDetail.status === "IN_TRANSIT") {
          updatedSteps[0]["status"] = "complete";
          updatedSteps[1]["status"] = "complete";
          updatedSteps[2]["status"] = "complete";
          updatedSteps[3]["status"] = "current";
        } else if (
          redemptionDetail.status === "DELIVERED" ||
          redemptionDetail.status === "COMPLETED"
        ) {
          updatedSteps[0]["status"] = "complete";
          updatedSteps[1]["status"] = "complete";
          updatedSteps[2]["status"] = "complete";
          updatedSteps[3]["status"] = "complete";
          updatedSteps[4]["status"] = "complete";
        }
      }
      setsteps(updatedSteps);
    }
  }, [redemptionDetail?.status, redemptionDetail?.rewardProduct?.type]);

  const handleCopyClick = async () => {
    const voucherId = redemptionDetail?.voucher?.voucher?.id;
    if (voucherId) {
      await navigator.clipboard.writeText(voucherId);
      setIsCopied(true);
      // Reset icon after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  };

  const renderStep = (step, stepIdx) => {
    if (step.status === "current") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group" aria-current="step">
            <span className="h-9 flex items-center" aria-hidden="true">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-blue-600 rounded-full">
                <span className="h-2.5 w-2.5 bg-blue-600 rounded-full" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase text-blue-600">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (step.status === "complete") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-blue-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full">
                <CheckIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (step.status === "failed") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-red-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-red-600 rounded-full">
                <XIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    return (
      <div>
        {stepIdx !== steps.length - 1 ? (
          <div
            className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
            aria-hidden="true"
          />
        ) : null}
        <div className="relative flex items-start group">
          <span className="h-9 flex items-center" aria-hidden="true">
            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-gray-300 rounded-full"></span>
          </span>
          <span className="ml-4 min-w-0 flex flex-col">
            <span className="text-xs font-semibold tracking-wide uppercase text-gray-500">
              {t(step.name)}
            </span>
            <span className="text-sm text-gray-500">{t(step.description)}</span>
          </span>
        </div>
      </div>
    );
  };

  const getStatusType = (status) => {
    switch (status) {
      case "PENDING":
      case "PACKED":
        return "alert";
      case "CONFIRMED":
      case "SHIPPED":
      case "IN_TRANSIT":
        return "info";
      case "DELIVERED":
      case "COMPLETED":
      case "ISSUED":
        return "success";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  if (redemptionDetailError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={redemptionDetailError?.message}
          errors={redemptionDetailError?.errors}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
      <div className="lg:col-start-3 lg:col-span-1">
        {redemptionDetailLoading ? (
          <div className="bg-white animate-pulse rounded-lg p-4 flex items-center">
            <div className="h-8 w-8 mr-2 bg-gray-300 rounded-full"></div>
            <div>
              <div className="h-4 w-24 my-1 bg-gray-300 rounded-md"></div>
              <div className="h-4 w-48 my-1 bg-gray-300 rounded-md"></div>
            </div>
          </div>
        ) : (
          <div>
            {steps.length > 0 && (
              <div className="bg-white rounded-lg p-4">
                <div className="flex justify-between items-center text-sm font-medium text-gray-900 mb-4">
                  <span>{t("orderStatus")}</span>
                  <Badge
                    title={redemptionDetail?.status?.toLowerCase() || "-"}
                    type={getStatusType(redemptionDetail?.status)}
                  />
                </div>
                {redemptionDetail.status === "FAILED" ? (
                  <div className="relative flex items-start group">
                    <span className="h-9 flex items-center">
                      <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-red-600 rounded-full">
                        <XIcon
                          className="w-5 h-5 text-white"
                          aria-hidden="true"
                        />
                      </span>
                    </span>
                    <span className="ml-4 min-w-0 flex flex-col">
                      <span className="text-xs font-semibold tracking-wide uppercase">
                        {t("Error")}
                      </span>
                      <span className="text-sm text-gray-500">
                        {t("rewardRedemptionFailed")}
                      </span>
                    </span>
                  </div>
                ) : (
                  <ol className="overflow-hidden">
                    {steps.map((step, stepIdx) => (
                      <li
                        key={step.name}
                        className={classNames(
                          stepIdx !== steps.length - 1 ? "pb-10" : "",
                          "relative"
                        )}
                      >
                        {renderStep(step, stepIdx)}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg lg:col-start-1 lg:col-span-2">
        <p className="text-sm font-medium text-gray-900 px-4 py-5 sm:px-6">
          {t("redemptionDetail")}
        </p>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Product")}
              </dt>
              {redemptionDetailLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  <a
                    href={`/rewards-product/${redemptionDetail?.rewardProduct?.id}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {redemptionDetail?.rewardProduct?.name || "-"}
                  </a>
                </dd>
              )}
            </div>
            <div className="sm:col-span-1 ">
              <dt className="text-sm font-medium text-gray-500">
                {t("point")}
              </dt>
              {redemptionDetailLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm font-medium text-gray-900">
                  {redemptionDetail?.rewardProduct?.point?.toLocaleString(
                    "id-ID"
                  ) || "-"}{" "}
                  {t("point")}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1 ">
              <dt className="text-sm font-medium text-gray-500">
                {t("Amount")}
              </dt>
              {redemptionDetailLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {redemptionDetail?.quantity || "-"}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1 ">
              <dt className="text-sm font-medium text-gray-500">
                {t("redemptionDate")}
              </dt>
              {redemptionDetailLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {dayjs(redemptionDetail?.createdAt)
                    .locale(i18n.language)
                    .format("DD MMM YYYY") || "-"}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1 ">
              <dt className="text-sm font-medium text-gray-500">
                {t("Order ID")}
              </dt>
              {redemptionDetailLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {redemptionDetail?.id || "-"}
                </dd>
              )}
            </div>
          </dl>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <p className="text-sm font-medium text-gray-900 mb-4">
            {t("contactInformation")}
          </p>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1 flex gap-2">
              <MailIcon className="h-4 w-4 text-gray-400" />
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Email")}
                </dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {redemptionDetail?.detail?.email || "-"}
                  </dd>
                )}
              </div>
            </div>
            <div className="sm:col-span-1 flex gap-2">
              <PhoneIcon className="h-4 w-4 text-gray-400" />
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Phone Number")}
                </dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatIndonesianPhone(redemptionDetail?.detail?.phone)}
                  </dd>
                )}
              </div>
            </div>
            <div className="sm:col-span-1 flex gap-2">
              <LocationMarkerIcon className="h-4 w-4 text-gray-400" />
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("shippingAddress")}
                </dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {redemptionDetail?.detail?.address?.street || "-"}
                  </dd>
                )}
              </div>
            </div>
          </dl>
        </div>
        {redemptionDetail?.voucher?.voucher?.id && (
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <p className="text-sm font-medium text-gray-900 mb-4">
              {t("voucherDetail")}
            </p>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1 ">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Code")}
                </dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    <button
                      className="flex gap-2 items-center font-medium transition-colors"
                      onClick={handleCopyClick}
                    >
                      {redemptionDetail?.voucher?.voucher?.id || "-"}
                      {isCopied ? (
                        <CheckIcon className="w-4 h-4 text-green-600" />
                      ) : (
                        <DuplicateIcon className="w-4 h-4" />
                      )}
                    </button>
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Nominal</dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    <NumericFormat
                      value={redemptionDetail?.voucher?.voucher?.total || ""}
                      displayType={"text"}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix={isAmount ? "Rp" : ""}
                      suffix={isAmount ? "" : "%"}
                    />
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Description")}
                </dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {redemptionDetail?.voucher?.voucher?.description || "-"}
                  </dd>
                )}
              </div>
            </dl>
          </div>
        )}
        {redemptionDetail?.shippingDetails &&
          redemptionDetail.rewardProduct.type === "PHYSICAL" && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <p className="text-sm font-medium text-gray-900 mb-4">
                {t("shippingDetail")}
              </p>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("shippingService")}
                  </dt>
                  {redemptionDetailLoading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      {redemptionDetail?.shippingDetails?.carrier || "-"}
                    </dd>
                  )}
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("trackingNumber")}
                  </dt>
                  {redemptionDetailLoading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      {redemptionDetail?.shippingDetails?.trackingNumber || "-"}
                    </dd>
                  )}
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("shipped")}
                  </dt>
                  {redemptionDetailLoading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      {redemptionDetail?.shippingDetails?.shippedAt
                        ? dayjs(redemptionDetail.shippingDetails.shippedAt)
                            .locale(i18n.language)
                            .format("DD MMM YYYY")
                        : "-"}
                    </dd>
                  )}
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("estimatedArrival")}
                  </dt>
                  {redemptionDetailLoading ? (
                    <DetailSkeleton />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">
                      {redemptionDetail?.shippingDetails?.estimatedDeliveryDate
                        ? dayjs(
                            redemptionDetail.shippingDetails
                              .estimatedDeliveryDate
                          )
                            .locale(i18n.language)
                            .format("DD MMM YYYY")
                        : "-"}
                    </dd>
                  )}
                </div>
              </dl>
            </div>
          )}
        {redemptionDetail?.rewardProduct?.type === "MOBILE_RELOAD" && (
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <p className="text-sm font-medium text-gray-900 mb-4">
              {t("mobileReloadDetail")}
            </p>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Nominal</dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    <NumericFormat
                      value={redemptionDetail?.rewardProduct?.point || ""}
                      displayType={"text"}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="Rp"
                    />
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("reference")}
                </dt>
                {redemptionDetailLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {redemptionDetail?.reference || "-"}
                  </dd>
                )}
              </div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardRedemptionDetail;
